/**
 * `bedrock-web-vanilla` — the framework-free adapter.
 *
 * One export, deliberately. The package exists to prove `bedrock-web-core` is usable with no UI
 * framework, and its content is that proof plus the one idiom server-rendered pages actually
 * repeat: find the slots a page carries, and attach behaviour to each of them once.
 *
 * There is no behaviour registry and no dispose lifecycle. Nothing in the reference application
 * disposes anything, there is no dynamic DOM to dispose for, and a lifecycle API here would be the
 * mini-framework this package exists to avoid (`.docs/components/frontend.md` section 6).
 */
/**
 * Records which behaviours an element already carries.
 *
 * A DOM attribute rather than a WeakMap: it survives the module being evaluated twice, which is
 * exactly what a dev-server hot update does. The value is a JSON array of the selectors that have
 * mounted — JSON rather than a space-separated list because a CSS selector may legitimately contain
 * spaces (`.a > .b`, `[data-x] input`), which a token list could not represent.
 */
const MOUNTED_ATTRIBUTE = 'data-bedrock-mounted';
/**
 * The selectors already mounted on an element.
 *
 * A malformed or hand-edited attribute is treated as "nothing mounted" rather than as an error:
 * the attribute is bookkeeping, and refusing to mount a page because someone typed into the DOM
 * would be a worse failure than mounting twice.
 */
function mountedSelectors(element) {
    const raw = element.getAttribute(MOUNTED_ATTRIBUTE);
    if (raw === null || raw.length === 0) {
        return [];
    }
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((entry) => typeof entry === 'string') : [];
    }
    catch {
        return [];
    }
}
/**
 * Attaches behaviour to every element matching a selector, at most once per selector per element.
 *
 * @param selector CSS selector for the slots a page may carry. It is also the behaviour's identity:
 * two different selectors may mount the same element, and the same selector never mounts it twice.
 * @param mount Runs once per matching element. Its exceptions propagate.
 * @param root Where to search; the document by default.
 *
 * @remarks
 * Zero matches is normal, not an error: most pages carry most slots, and a page that carries none
 * simply gets no behaviour.
 *
 * A mount that throws leaves its element unmarked for that selector — it has not mounted, so a
 * later call may legitimately try again — and the exception reaches the caller. **The remaining
 * matched elements in that call are therefore not visited**: a behaviour that fails on one slot is
 * a defect to surface, not something to work around by continuing with a half-initialised page.
 */
export function mountAll(selector, mount, root = document) {
    // Materialised before mounting, so a mount that inserts matching markup of its own cannot make
    // this walk visit its output in the same pass.
    const elements = [...root.querySelectorAll(selector)];
    for (const element of elements) {
        const mounted = mountedSelectors(element);
        if (mounted.includes(selector)) {
            continue;
        }
        // Marked only after the mount returns: a throwing mount has not mounted, and marking it first
        // would silently skip the element on every later attempt.
        mount(element);
        element.setAttribute(MOUNTED_ATTRIBUTE, JSON.stringify([...mounted, selector]));
    }
}
//# sourceMappingURL=index.js.map